/**
 * Browser-local Pages store (localStorage) for playground / nonproduction acceptance
 * when the Django Pages API is not mounted yet.
 *
 * Mirrors the server contract enough to create → draft → publish → refresh → get.
 * Never used for production customer data when WORKLOAD_PAGES API is available.
 */

const KEY = 'workload_pages_local_v1';

function load() {
  try {
    return JSON.parse(localStorage.getItem(KEY) || '{}');
  } catch {
    return {};
  }
}

function save(db) {
  localStorage.setItem(KEY, JSON.stringify(db));
}

function newId(prefix) {
  return `${prefix}_${Math.random().toString(36).slice(2, 10)}`;
}

export const localPagesStore = {
  enabled() {
    if (typeof window === 'undefined') return false;
    if (process.env.REACT_APP_WORKLOAD_PAGES_USE_LOCAL === 'false') return false;
    // Default on in non-production; opt-in in production via env
    if (process.env.NODE_ENV === 'production') {
      return process.env.REACT_APP_WORKLOAD_PAGES_USE_LOCAL === 'true';
    }
    return true;
  },

  createPage({ tenant_id, slug, title, visibility }) {
    const db = load();
    const page_id = `${tenant_id}/${slug}`;
    if (db.pages && db.pages[page_id]) throw new Error('page already exists');
    db.pages = db.pages || {};
    db.drafts = db.drafts || {};
    db.versions = db.versions || {};
    db.manifests = db.manifests || {};
    db.datasets = db.datasets || {};
    db.pages[page_id] = {
      page_id,
      tenant_id,
      slug,
      title: title || slug,
      visibility: visibility || { policy: 'private' },
      published_canvas_version: null,
      active_manifest_id: null,
      meta_version: 1,
    };
    db.drafts[page_id] = {
      schema_version: 1,
      page_id,
      tenant_id,
      title: title || slug,
      visibility: visibility || { policy: 'private' },
      dataset_slots: [],
      root: {
        id: 'root',
        component: 'builtin:Stack',
        version: '1.0.0',
        props: {},
        children: [],
      },
      document_kind: 'draft',
    };
    save(db);
    return { page: db.pages[page_id], draft: db.drafts[page_id] };
  },

  updateDraft(page_id, canvas) {
    const db = load();
    if (!db.pages || !db.pages[page_id]) throw new Error('page not found');
    const body = { ...canvas, page_id, document_kind: 'draft', immutable: false, canvas_version: null };
    db.drafts[page_id] = body;
    db.pages[page_id].title = body.title || db.pages[page_id].title;
    save(db);
    return { draft: body };
  },

  publish(page_id, { initial_snapshots } = {}) {
    const db = load();
    const page = db.pages && db.pages[page_id];
    const draft = db.drafts && db.drafts[page_id];
    if (!page || !draft) throw new Error('page/draft not found');
    const next = (page.published_canvas_version || 0) + 1;
    const immutable = {
      ...JSON.parse(JSON.stringify(draft)),
      canvas_version: next,
      document_kind: 'canvas_version',
      immutable: true,
      version_state: 'published',
    };
    db.versions[`${page_id}@${next}`] = immutable;

    let manifest_id = page.active_manifest_id;
    if (initial_snapshots && initial_snapshots.length) {
      const entries = [];
      initial_snapshots.forEach((snap) => {
        const slot = (draft.dataset_slots || []).find((s) => s.id === snap.slot);
        const dataset_id = (slot && slot.dataset_id) || newId('ds');
        const version = 1;
        db.datasets[`${dataset_id}@${version}`] = {
          dataset_id,
          version,
          status: snap.status || 'validated',
          data: snap.data || {},
        };
        entries.push({
          slot: snap.slot,
          dataset_id,
          dataset_version: version,
          status: snap.status || 'validated',
        });
      });
      manifest_id = newId('man');
      db.manifests[manifest_id] = {
        manifest_id,
        page_id,
        canvas_version: next,
        entries,
      };
    }

    page.published_canvas_version = next;
    page.active_manifest_id = manifest_id;
    page.meta_version = (page.meta_version || 1) + 1;
    save(db);
    return { page, canvas_version: next, manifest_id };
  },

  refresh(page_id, snapshots) {
    const db = load();
    const page = db.pages && db.pages[page_id];
    if (!page || !page.published_canvas_version) throw new Error('not published');
    const prevId = page.active_manifest_id;
    const prev = prevId ? db.manifests[prevId] : null;
    const canvas = db.versions[`${page_id}@${page.published_canvas_version}`];
    const entries = [];
    let failedRequired = false;

    snapshots.forEach((snap) => {
      const slotMeta = (canvas.dataset_slots || []).find((s) => s.id === snap.slot);
      const required = slotMeta && (slotMeta.required || slotMeta.on_missing === 'block_publish');
      if (required && snap.status === 'failed') failedRequired = true;
      const dataset_id =
        (slotMeta && slotMeta.dataset_id) ||
        (prev &&
          (prev.entries || []).find((e) => e.slot === snap.slot) &&
          (prev.entries || []).find((e) => e.slot === snap.slot).dataset_id) ||
        newId('ds');
      const version = Date.now() % 100000;
      db.datasets[`${dataset_id}@${version}`] = {
        dataset_id,
        version,
        status: snap.status || 'validated',
        data: snap.data || {},
      };
      entries.push({
        slot: snap.slot,
        dataset_id,
        dataset_version: version,
        status: snap.status || 'validated',
      });
    });

    if (prev) {
      (prev.entries || []).forEach((e) => {
        if (!entries.find((x) => x.slot === e.slot)) entries.push(e);
      });
    }

    if (failedRequired) {
      return { ok: false, activated: false, page };
    }

    const manifest_id = newId('man');
    db.manifests[manifest_id] = {
      manifest_id,
      page_id,
      canvas_version: page.published_canvas_version,
      entries,
    };
    page.active_manifest_id = manifest_id;
    save(db);
    return { ok: true, activated: true, page, manifest: db.manifests[manifest_id] };
  },

  getPublished(page_id) {
    const db = load();
    const page = db.pages && db.pages[page_id];
    if (!page || !page.published_canvas_version) return null;
    const canvas = db.versions[`${page_id}@${page.published_canvas_version}`];
    const manifest = page.active_manifest_id ? db.manifests[page.active_manifest_id] : null;
    const datasets = {};
    (manifest && manifest.entries ? manifest.entries : []).forEach((e) => {
      const snap = db.datasets[`${e.dataset_id}@${e.dataset_version}`];
      if (snap) {
        datasets[e.dataset_id] = snap;
        datasets[`${e.dataset_id}@${e.dataset_version}`] = snap;
      }
    });
    return { kind: 'published', page, canvas, manifest, datasets };
  },
};

export default localPagesStore;
